<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'

const { t } = useI18n()
const authStore = useAuthStore()
const uiStore = useUiStore()

const navItems = computed(() => [
  { path: '/', label: 'nav.home', requiresAuth: false },
  { path: '/recipes', label: 'nav.recipes', requiresAuth: false },
  { path: '/planner', label: 'nav.planner', requiresAuth: true },
  { path: '/shopping', label: 'nav.shopping', requiresAuth: true },
  { path: '/notes', label: 'nav.notes', requiresAuth: true },
  { path: '/stats', label: 'nav.stats', requiresAuth: true },
  { path: '/settings', label: 'nav.settings', requiresAuth: true },
  { path: '/admin', label: 'nav.admin', requiresAuth: true, requiresAdmin: true }
])

const visibleNavItems = computed(() => {
  return navItems.value.filter(item => {
    // Check auth requirement
    if (item.requiresAuth && !authStore.isAuthenticated) return false
    // Check admin requirement
    if (item.requiresAdmin && !authStore.isAdmin) return false
    return true
  })
})

function closeSidebar() {
  uiStore.closeSidebar()
}

function handleNavClick() {
  // Закрываем сайдбар только на мобильных устройствах
  if (window.innerWidth <= 1024) {
    closeSidebar()
  }
}

async function handleLogout() {
  await authStore.logout()
  closeSidebar()
}
</script>

<template>
  <!-- Desktop Sidebar (always visible) -->
  <aside class="sidebar desktop-sidebar">
    <div class="sidebar-header">
      <router-link to="/" class="logo">
        {{ t('app.title') }}
      </router-link>
    </div>
    
    <nav class="sidebar-nav">
      <router-link
        v-for="item in visibleNavItems"
        :key="item.path"
        :to="item.path"
        class="nav-item"
      >
        <span class="nav-label">{{ t(item.label) }}</span>
      </router-link>
    </nav>
    
    <div class="sidebar-footer">
      <div v-if="authStore.isAuthenticated" class="user-section">
        <div class="user-info">
          <div class="user-avatar">
            {{ authStore.user?.name?.charAt(0)?.toUpperCase() || 'U' }}
          </div>
          <span class="user-name">{{ authStore.user?.name }}</span>
        </div>
        <button class="logout-btn" @click="handleLogout">
          {{ t('nav.logout') }}
        </button>
      </div>
      
      <div v-else class="auth-buttons">
        <router-link to="/login" class="login-btn">
          {{ t('nav.login') }}
        </router-link>
        <router-link to="/register" class="register-btn">
          {{ t('nav.register') }}
        </router-link>
      </div>
    </div>
  </aside>

  <!-- Mobile Sidebar (overlay) -->
  <Teleport to="body">
    <Transition name="sidebar">
      <div v-if="uiStore.sidebarOpen" class="sidebar-backdrop" @click="closeSidebar">
        <aside class="sidebar mobile-sidebar" @click.stop>
          <div class="sidebar-header">
            <router-link to="/" class="logo" @click="handleNavClick">
              {{ t('app.title') }}
            </router-link>
            <button class="close-btn" @click="closeSidebar" aria-label="Close menu">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
          
          <nav class="sidebar-nav">
            <router-link
              v-for="item in visibleNavItems"
              :key="item.path"
              :to="item.path"
              class="nav-item"
              @click="handleNavClick"
            >
              <span class="nav-label">{{ t(item.label) }}</span>
            </router-link>
          </nav>
          
          <div class="sidebar-footer">
            <div v-if="authStore.isAuthenticated" class="user-section">
              <div class="user-info">
                <div class="user-avatar">
                  {{ authStore.user?.name?.charAt(0)?.toUpperCase() || 'U' }}
                </div>
                <span class="user-name">{{ authStore.user?.name }}</span>
              </div>
              <button class="logout-btn" @click="handleLogout">
                {{ t('nav.logout') }}
              </button>
            </div>
            
            <div v-else class="auth-buttons">
              <router-link to="/login" class="login-btn" @click="handleNavClick">
                {{ t('nav.login') }}
              </router-link>
              <router-link to="/register" class="register-btn" @click="handleNavClick">
                {{ t('nav.register') }}
              </router-link>
            </div>
          </div>
        </aside>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* Desktop Sidebar */
.desktop-sidebar {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 240px;
  background: var(--color-background);
  border-right: 1px solid var(--color-border);
  flex-direction: column;
  z-index: 50;
}

@media (min-width: 1025px) {
  .desktop-sidebar {
    display: flex;
  }
}

/* Mobile Sidebar Backdrop */
.sidebar-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--color-overlay);
  z-index: 200;
}

.mobile-sidebar {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 280px;
  max-width: 85vw;
  background: var(--color-background);
  display: flex;
  flex-direction: column;
  box-shadow: 4px 0 20px var(--color-shadow);
}

@media (min-width: 1025px) {
  .sidebar-backdrop {
    display: none;
  }
}

/* Shared styles */
.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid var(--color-border);
  min-height: 64px;
}

.logo {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-primary);
  text-decoration: none;
}

.logo:hover {
  color: var(--color-primary-dark);
}

.close-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  color: var(--color-text-secondary);
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background: var(--color-background-secondary);
  color: var(--color-text);
}

.desktop-sidebar .close-btn {
  display: none;
}

.sidebar-nav {
  flex: 1;
  padding: 16px 12px;
  overflow-y: auto;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  text-decoration: none;
  color: var(--color-text);
  border-radius: 8px;
  margin-bottom: 4px;
  transition: background 0.2s, color 0.2s;
  font-size: 0.95rem;
  font-weight: 500;
}

.nav-item:hover {
  background: var(--color-background-secondary);
}

.nav-item.router-link-active {
  background: var(--color-primary-light);
  color: var(--color-primary);
}

.nav-label {
  font-size: inherit;
  font-weight: inherit;
}

.sidebar-footer {
  padding: 16px;
  border-top: 1px solid var(--color-border);
}

.user-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-avatar {
  width: 36px;
  height: 36px;
  background: var(--color-primary);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 600;
}

.user-name {
  font-weight: 500;
  color: var(--color-text);
  font-size: 0.9rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.logout-btn {
  width: 100%;
  padding: 10px;
  background: transparent;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  color: var(--color-text);
  font-size: 0.875rem;
  cursor: pointer;
  transition: background 0.2s;
}

.logout-btn:hover {
  background: var(--color-background-secondary);
}

.auth-buttons {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.login-btn,
.register-btn {
  display: block;
  width: 100%;
  padding: 10px;
  text-align: center;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.875rem;
  transition: background 0.2s;
}

.login-btn {
  background: var(--color-primary);
  color: white;
}

.login-btn:hover {
  background: var(--color-primary-dark);
}

.register-btn {
  background: transparent;
  border: 1px solid var(--color-border);
  color: var(--color-text);
}

.register-btn:hover {
  background: var(--color-background-secondary);
}

/* Transitions */
.sidebar-enter-active,
.sidebar-leave-active {
  transition: opacity 0.3s ease;
}

.sidebar-enter-active .mobile-sidebar,
.sidebar-leave-active .mobile-sidebar {
  transition: transform 0.3s ease;
}

.sidebar-enter-from,
.sidebar-leave-to {
  opacity: 0;
}

.sidebar-enter-from .mobile-sidebar,
.sidebar-leave-to .mobile-sidebar {
  transform: translateX(-100%);
}
</style>
